import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { journeyData } from "../Data";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const JourneySection = () => {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  
  // 1. Create a reference to the main section to monitor visibility
  const sectionRef = useRef(null);
  const isSectionInView = useInView(sectionRef, { once: true, amount: 0.2 });
  
  // Keep track of the swiper instance
  const [swiperInstance, setSwiperInstance] = useState(null);

  // 2. Trigger autoplay from the very first slide ONLY when the section comes into view
  useEffect(() => {
    if (isSectionInView && swiperInstance) {
      swiperInstance.slideTo(0, 0); // Hard reset to slide index 0 instantly
      swiperInstance.autoplay.start(); // Start running autoplay now
    }
  }, [isSectionInView, swiperInstance]);

  const fadeUp = {
    hidden: { opacity: 0, y: 35 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <>

      <section ref={sectionRef} className="journey-section container">
        
        {/* TOP CONTENT */}
        <motion.h2
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          OUR INSPIRATIONAL JOURNEY
        </motion.h2>

        <motion.p
          className="section-text"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          Our journey is a story of continuous growth, innovation, and
          commitment to excellence. From our early beginnings to becoming a
          trusted name in the global mineral industry, each milestone reflects
          our dedication to quality, sustainability, and customer satisfaction.
        </motion.p>

        {/* SLIDER CONTAINER */}
        <motion.div
          className="journey-slider"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeUp}
        >
          {/* CUSTOM ARROWS */}
          <div ref={prevRef} className="custom-arrow prev-arrow">
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.1 }}
            >
              &#10094;
            </motion.span>
          </div>

          <div ref={nextRef} className="custom-arrow next-arrow">
            <motion.span
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.1 }}
            >
              &#10095;
            </motion.span>
          </div>

          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            slidesPerView={3}
            spaceBetween={50}
            loop={false}
            // Turn off automatic start initially
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
              stopOnLastSlide: true,
            }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            pagination={{
              clickable: true,
            }}
            // Capture instance and initialize selectors safely
            onInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
              swiper.navigation.init();
              swiper.navigation.update();
              
              // Pause immediately on generation so it won't run in the background
              swiper.autoplay.stop(); 
              setSwiperInstance(swiper);
            }}
            breakpoints={{
              0: { slidesPerView: 1 },
              576: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              992: { slidesPerView: 2 },
              1200: { slidesPerView: 3 },
            }}
            className="our-gallery-swiper"
          >
            {journeyData.map((item, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  className="journey-card"
                  initial={{ opacity: 0, y: 80 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: false, amount: 0.4 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  whileHover={{ y: -8 }}
                >
                  <div className="journey-image">
                    <motion.img
                      src={item.icon}
                      alt={`${item.desc} | Iris Impulse India Pvt. Ltd.`}
                      whileHover={{ scale: 1.5 }}
                      transition={{ duration: 0.35 }}
                    />
                  </div>

                  <div className="journey-text">
                    <h3 className="card-heading">{item.title}</h3>
                    <p className="section-text">{item.desc}</p>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </section>
    </>
  );
};

export default JourneySection;